import { useQuery, useMutation } from '@apollo/client'
import { ToastContext } from 'contexts'
import { useContext } from 'react'

import createRateLimitGql from 'gql/rateLimit/createRateLimit.gql'
import updateRateLimitGql from 'gql/rateLimit/updateRateLimit.gql'
import deleteRateLimitGql from 'gql/rateLimit/deleteRateLimit.gql'
import rateLimitsGql from 'gql/rateLimit/rateLimits.gql'
import rateLimitByIdGql from 'gql/rateLimit/rateLimitById.gql'
import {
    RateLimitInput,
    // UseCreateRateLimitService,
    UseDeleteRateLimitService,
    UseRateLimitByIdReturn,
    UseRateLimitsReturn,
    UseUpdateRateLimitService,
} from 'types/rateLimit'

/**
 * Function that creates a rate limit based on the input provided.
 *
 * @param {RateLimitInput} input - The input data for the rate limit.
 * @return {Promise<UseCreateRateLimitService>} The created rate limit.
 */
export const useCreateRateLimitService = (subnet_id: string | undefined) => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(createRateLimitGql, {
        update(
            cache,
            {
                data: {
                    createRateLimit: { rate_limit },
                },
            },
        ) {
            const queryResponse: any = cache.readQuery({
                query: rateLimitsGql,
                variables: { subnet_id: subnet_id },
            })
            // console.log('queryResponse', queryResponse)
            cache.writeQuery({
                query: rateLimitsGql,
                variables: { subnet_id: subnet_id },
                data: { rateLimits: [rate_limit, ...queryResponse.rateLimits] },
            })
        },
    })

    const createRateLimit = async (input: RateLimitInput) => {
        try {
            const {
                data: { createRateLimit },
            } = await mutation({
                variables: {
                    input: input,
                },
            })

            return createRateLimit
        } catch (error) {
            setToast({
                message: error?.message ?? 'Error creating Rate Limit',
                type: 'negative',
                open: true,
            })
            throw error // Rethrow the error to propagate it to the caller
        }
    }

    return { createRateLimit, loading }
}

/**
 * Function that updates a rate limit based on the provided rate limit ID and input data.
 *
 * @param {string} rate_limit_id - The ID of the rate limit to update.
 * @param {RateLimitInput} input - The input data for updating the rate limit.
 * @return {Promise<UseUpdateRateLimitService>} The updated rate limit.
 */
export const useUpdateRateLimitService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(updateRateLimitGql, {
        update(
            cache,
            {
                data: {
                    updateRateLimit: { rate_limit },
                },
            },
        ) {
            cache.modify({
                fields: {
                    rateLimits(existingRateLimits = [], { readField }) {
                        return existingRateLimits.map((rateLimitRef: any) =>
                            rate_limit.id === readField('id', rateLimitRef)
                                ? rate_limit
                                : rateLimitRef,
                        )
                    },
                },
            })
        },
    })

    const updateRateLimit = async (
        rate_limit_id: string,
        input: RateLimitInput,
    ): Promise<UseUpdateRateLimitService> => {
        try {
            const {
                data: { updateRateLimit },
            } = await mutation({
                variables: {
                    rate_limit_id,
                    input: input,
                },
            })

            return updateRateLimit
        } catch (error) {
            setToast({
                message: error?.message ?? 'Error updating Rate Limit',
                type: 'negative',
                open: true,
            })
            throw error // Rethrow the error to propagate it to the caller
        }
    }

    return { updateRateLimit, loading }
}

/**
 * Deletes a rate limit based on the provided rate limit ID.
 *
 * @param {string} rate_limit_id - The ID of the rate limit to delete.
 * @return {Promise<UseDeleteRateLimitService>} The deleted rate limit.
 */
export const useDeleteRateLimitService = () => {
    const { setToast } = useContext(ToastContext)
    const [mutation, { loading }] = useMutation(deleteRateLimitGql, {
        update(
            cache,
            {
                data: {
                    deleteRateLimit: { rate_limit },
                },
            },
        ) {
            cache.modify({
                fields: {
                    rateLimits(existingRateLimits = [], { readField }) {
                        return existingRateLimits.filter(
                            (rateLimitRef: any) => rate_limit.id !== readField('id', rateLimitRef),
                        )
                    },
                },
            })
        },
    })

    const deleteRateLimit = async (rate_limit_id: string): Promise<UseDeleteRateLimitService> => {
        try {
            const {
                data: { deleteRateLimit },
            } = await mutation({
                variables: {
                    rate_limit_id,
                },
            })

            return deleteRateLimit
        } catch (error) {
            setToast({
                message: error?.message ?? 'Error deleting Rate Limit',
                type: 'negative',
                open: true,
            })
            throw error // Rethrow the error to propagate it to the caller
        }
    }

    return { deleteRateLimit, loading }
}

/**
 * Fetches rate limits using the `rateLimitsGql` query.
 *
 * @return {UseRateLimitsReturn} An object containing the fetched rate limits, error, loading state, and a refetch function.
 */
export const useRateLimits = (subnet_id: string | undefined): UseRateLimitsReturn => {
    const { data, error, loading, refetch } = useQuery(rateLimitsGql, {
        fetchPolicy: 'cache-and-network',
        variables: { subnet_id },
    })

    return {
        data: data?.rateLimits || [],
        error,
        loading,
        refetch,
    }
}

/**
 * Fetches a rate limit by its ID using the `rateLimitByIdGql` query.
 *
 * @return {UseRateLimitByIdReturn} An object containing the fetched rate limit, error, loading state, and a refetch function.
 */
export const useRateLimitById = (): UseRateLimitByIdReturn => {
    const { data, error, loading, refetch } = useQuery(rateLimitByIdGql, {
        fetchPolicy: 'cache-and-network',
    })

    return {
        data: data?.rateLimitById || {},
        error,
        loading,
        refetch,
    }
}
